import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TermoResponse } from '../../../core/models/termo';
import { PdfDocument } from '../../../shared/components/pdf-document/pdf-document';
import { Steps } from '../../../shared/components/steps/steps';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { ToastService } from '../../../shared/services/toast-service';
import { base64ParaPdf } from '../../../shared/utils/pdf';

@Component({
  imports: [PdfDocument, Steps],
  selector: 'app-assinatura-termo',
  styleUrl: './assinatura-termo.scss',
  templateUrl: './assinatura-termo.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AssinaturaTermo implements OnInit {
  private readonly router = inject(Router);
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);

  readonly stepAtual = signal(7);
  readonly totalSteps = signal(7);
  /** Falha ao buscar o termo. */
  readonly error = signal(false);
  /** Dados do termo retornados pela API (nome, cpf e tokens usados na assinatura). */
  readonly termo = signal<TermoResponse | null>(null);
  /** Endereço local (blob:) do PDF do termo; enquanto vazio, o visualizador não é renderizado. */
  readonly pdfSrc = signal<string | undefined>(undefined);
  /** O botão "Assinar termo" só é habilitado depois de marcar "Li e estou de acordo". */
  readonly aceiteTermo = signal(false);
  /** Modal "Solicitação enviada com sucesso!", aberto quando a assinatura é registrada. */
  readonly openModalSucesso = signal(false);

  constructor() {
    // Libera da memória o PDF criado com URL.createObjectURL ao sair da tela.
    inject(DestroyRef).onDestroy(() => this.liberarPdf());
  }

  ngOnInit(): void {
    void this.getTermo();
  }

  /** O vivest-checkbox dispara `change` e expõe o estado na propriedade `checked`. */
  protected onAceiteChange(event: Event): void {
    this.aceiteTermo.set((event.target as HTMLElement & { checked: boolean }).checked);
  }

  /** Busca o termo e exibe o PDF, que vem em base64. */
  async getTermo(): Promise<void> {
    this.loaderService.showLoader('Carregando...');

    try {
      const { body } = await this.httpClientService.getTermo();
      if (!body?.pdf) throw new Error('Termo sem PDF');

      this.termo.set(body);
      this.liberarPdf();
      this.pdfSrc.set(URL.createObjectURL(base64ParaPdf(body.pdf)));
    } catch (e) {
      console.error(e);
      this.error.set(true);
      this.toastService.show('Não foi possível carregar o termo. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  onReturn() {
    this.router.navigate(['/instituto-online/resumo']);
  }

  /**
   * Assina o termo enviando o processId retornado pelo getTermo e, com sucesso, abre o modal
   * de confirmação. Sem o processId (o termo não carregou), não chama a API.
   */
  async onSubmit(): Promise<void> {
    if (!this.aceiteTermo()) return;

    const processId = this.termo()?.processId;
    // Compara com null para não bloquear um processId 0, que também é válido.
    if (processId == null) {
      this.toastService.show('Não foi possível assinar o termo. Tente novamente.', 'error');
      return;
    }

    this.loaderService.showLoader('Enviando...');

    try {
      await this.httpClientService.assinarTermo(processId);
      this.openModalSucesso.set(true);
    } catch {
      this.toastService.show('Não foi possível assinar o termo. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  closeModalSucesso(): void {
    this.openModalSucesso.set(false);
  }

  /** Fecha o modal e segue para a próxima etapa, com os detalhes da solicitação. */
  verDetalhes(): void {
    this.closeModalSucesso();
    this.router.navigate(['/instituto-online/detalhes']);
  }

  private liberarPdf(): void {
    const src = this.pdfSrc();
    if (src) URL.revokeObjectURL(src);
  }
}
